import _ from 'lodash'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { SidebarCategoryMenu } from '@/components/layout/sidebar/SidebarCategoryMenu'
import { SidebarLogoBanner } from '@/components/layout/sidebar/SidebarLogoBanner'
import { SidebarNavigationMenu } from '@/components/layout/sidebar/SidebarNavigationMenu'
import { SidebarSectionTab } from '@/components/layout/sidebar/SidebarSectionTab'
import { CASINO_PROVIDERS_NAV_TYPE, CASINO_TYPE_LOBBY, DEFAULT_CASINO_TYPES } from '@/config/casino'
import { SECTION_TABS } from '@/config/sidebar'
import { cn } from '@/lib/utils'
import { useGetLobbyCasinoTypesQuery } from '@/store/api/casinoApi'
import { useGetSportsbooksQuery } from '@/store/api/sportsbookApi'
import { useSidebarUI } from '@/context/SidebarUIContext'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setActiveSection, clearActiveSection } from '@/store/slices/configSlice'
import { selectSelectedCasinoTypeId, clearSelectedCasinoType, setSelectedCasinoType } from '@/store/slices/casinoSlice'
import { selectSelectedSportsbookId, clearSelectedSportsbook, setSelectedSportsbook } from '@/store/slices/sportsbookSlice'
import type { CasinoTypeId } from '@/types/casino'
import type { SidebarSection } from '@/types/config'

export function Sidebar() {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const activeSection = useAppSelector((state) => state.config.activeSection)
	const menuSection = useAppSelector((state) => state.config.menuSection)
	const selectedCasinoTypeId = useAppSelector(selectSelectedCasinoTypeId)
	const selectedSportsbookId = useAppSelector(selectSelectedSportsbookId)
	const { isMobileOpen, closeMobileSidebar } = useSidebarUI()
	const { data: lobbyTypesResponse } = useGetLobbyCasinoTypesQuery()
	const { data: sportsbooksResponse } = useGetSportsbooksQuery(undefined, { skip: menuSection !== 'sport' })

	const casinoMenuTypes = useMemo(() => {
		const defaultTypeIds = _.map(DEFAULT_CASINO_TYPES, 'id')
		const defaultTypes = DEFAULT_CASINO_TYPES.map((type) => ({
			id: type.id,
			label: type.id === CASINO_TYPE_LOBBY ? 'All Games' : type.label,
			icon: type.icon,
			count: undefined as number | undefined,
		}))
		const lobbyTypes = (lobbyTypesResponse?.data ?? [])
			.filter((type) => !_.includes(defaultTypeIds, type.id) && type.id !== CASINO_PROVIDERS_NAV_TYPE.id)
			.map((type) => ({
				id: type.id,
				label: _.capitalize(type.type_name),
				icon: type.icon,
				count: type.casinos_count,
			}))

		return [
			...defaultTypes,
			...lobbyTypes,
			{
				id: CASINO_PROVIDERS_NAV_TYPE.id,
				label: CASINO_PROVIDERS_NAV_TYPE.label,
				icon: CASINO_PROVIDERS_NAV_TYPE.icon,
				count: undefined as number | undefined,
			},
		]
	}, [lobbyTypesResponse?.data])

	const sportMenuItems = useMemo(
		() =>
			(sportsbooksResponse?.data ?? []).map((sportsbook) => ({
				id: sportsbook.id,
				label: _.capitalize(sportsbook.sportsbook_name),
				icon: null,
			})),
		[sportsbooksResponse?.data],
	)

	const handleSectionTabClick = (key: SidebarSection) => {
		dispatch(setActiveSection(key))

		if (key === 'casino') {
			navigate('/casino')
		}

		if (key === 'sport') {
			navigate('/sportsbook')
		}

		closeMobileSidebar()
	}

	const handleCasinoTypeClick = (typeId: CasinoTypeId) => {
		dispatch(setSelectedCasinoType(typeId))
		dispatch(setActiveSection('casino'))
		navigate('/casino')
		closeMobileSidebar()
	}

	const handleSportMenuClick = (sportsbookId: string) => {
		dispatch(setSelectedSportsbook(sportsbookId))
		dispatch(setActiveSection('sport'))
		navigate('/sportsbook')
		closeMobileSidebar()
	}

	const handleOriginalGameTypeClick = (gameId: string) => {
		dispatch(setActiveSection('casino'))
		navigate(`/original/play/${gameId}`)
		closeMobileSidebar()
	}

	const deactivateSectionMenus = () => {
		dispatch(clearSelectedCasinoType())
		dispatch(clearSelectedSportsbook())
		dispatch(clearActiveSection())
	}

	const handleLogoClick = () => {
		deactivateSectionMenus()
		navigate('/')
		closeMobileSidebar()
	}

	const handleNavItemClick = () => {
		deactivateSectionMenus()
		closeMobileSidebar()
	}

	return (
		<>
			{isMobileOpen && <div className='fixed inset-0 z-40 bg-black/60 lg:hidden' onClick={closeMobileSidebar} />}
			<aside
				className={cn(
					'fixed inset-y-4 left-4 z-[1000] flex w-[300px] max-w-[85vw] flex-col overflow-hidden rounded-2xl border border-border bg-background transition-transform lg:sticky lg:top-4 lg:z-0 lg:h-[96svh] lg:max-w-none lg:translate-x-0',
					isMobileOpen ? 'translate-x-0' : '-translate-x-[120%]',
				)}
			>
				<SidebarLogoBanner activeSection={activeSection} onClick={handleLogoClick} />

				<div className='flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4'>
					<div className='grid shrink-0 grid-cols-2 gap-2'>
						{SECTION_TABS.map((tab) => (
							<SidebarSectionTab key={tab.key} tab={tab} isActive={activeSection === tab.key} onClick={() => handleSectionTabClick(tab.key)} />
						))}
					</div>

					{activeSection ? (
						<div className='flex flex-col gap-5'>
							<SidebarCategoryMenu
								menuSection={menuSection}
								casinoMenuTypes={casinoMenuTypes}
								sportMenuItems={sportMenuItems}
								selectedCasinoTypeId={selectedCasinoTypeId}
								selectedSportsbookId={selectedSportsbookId}
								onCasinoTypeClick={handleCasinoTypeClick}
								onSportMenuClick={handleSportMenuClick}
								onOriginalGameTypeClick={handleOriginalGameTypeClick}
							/>
						</div>
					) : null}

					<SidebarNavigationMenu onItemClick={handleNavItemClick} />
				</div>
			</aside>
		</>
	)
}
